import { useCallback, useState } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import type { Race } from '../../types';
import { useGeoJSON } from '../../hooks';
import { RegionOverlay } from './RegionOverlay';
import { RegionFocusHandler } from './RegionFocusHandler';
import { RaceMarkerCluster } from './RaceMarkerCluster';
import { MapLibreTileLayer } from './MapLibreTileLayer';
import { AsymmetricZoomHandler } from './AsymmetricZoomHandler';
import './LeafletMap.css';

interface KoreaMapProps {
  races: Race[];
  selectedRace: Race | null;
  onRaceSelect: (race: Race | null) => void;
  onRaceHover: (race: Race | null) => void;
  highlightedRegions?: string[];
  hoveredRaceId?: string | null;
  onRegionClick?: (regionName: string) => void;
}

const KOREA_CENTER: [number, number] = [36.0, 127.5];
const DEFAULT_ZOOM = 7;
const MIN_ZOOM = 6;
const MAX_ZOOM = 18;

export function KoreaMap({
  races,
  selectedRace,
  onRaceSelect,
  onRaceHover,
  highlightedRegions,
  hoveredRaceId,
  onRegionClick,
}: KoreaMapProps) {
  const { data: geoData, loading, error } = useGeoJSON('/data/geo/korea.geojson');
  const [useVectorTiles, setUseVectorTiles] = useState(true);
  const handleTileError = useCallback(() => setUseVectorTiles(false), []);

  if (loading) {
    return (
      <div className="leaflet-map-container">
        <div className="leaflet-map-loading">지도를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaflet-map-container">
        <div className="leaflet-map-error">지도를 불러올 수 없습니다: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="leaflet-map-container">
      <MapContainer
        center={KOREA_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        zoomControl={false}
        scrollWheelZoom={false}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomControl position="bottomright" />
        <AsymmetricZoomHandler />
        {useVectorTiles ? (
          <MapLibreTileLayer
            style="https://tiles.openfreemap.org/styles/positron"
            attribution='&copy; <a href="https://openfreemap.org">OpenFreeMap</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            onError={handleTileError}
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        {geoData && (
          <>
            <RegionOverlay
              geoData={geoData}
              highlightedRegions={highlightedRegions}
              onRegionClick={onRegionClick}
            />
            <RegionFocusHandler
              geoData={geoData}
              focusedRegions={highlightedRegions}
              defaultCenter={KOREA_CENTER}
              defaultZoom={DEFAULT_ZOOM}
            />
          </>
        )}
        <RaceMarkerCluster
          races={races}
          selectedRace={selectedRace}
          onRaceSelect={onRaceSelect}
          onRaceHover={onRaceHover}
          hoveredRaceId={hoveredRaceId}
        />
      </MapContainer>
    </div>
  );
}
